/**
 * Distances in the units the reader uses: feet in the US (and the two other
 * countries that never moved to metric), metres everywhere else. Read from
 * the device locale's region.
 */
const IMPERIAL_REGIONS = new Set(['US', 'LR', 'MM']);

/**
 * What the reader picked in Settings. Display only: everything is recorded
 * in centimetres and degrees, whatever is shown.
 */
export type UnitPreference = 'auto' | 'metric' | 'imperial';
const KEY = 'erectus.units';

let preference: UnitPreference = (() => {
  try {
    const stored = window.localStorage.getItem(KEY);
    return stored === 'metric' || stored === 'imperial' ? stored : 'auto';
  } catch {
    return 'auto';
  }
})();

export function unitPreference(): UnitPreference {
  return preference;
}

export function setUnitPreference(next: UnitPreference): void {
  preference = next;
  try {
    window.localStorage.setItem(KEY, next);
  } catch {
    // The account copy (settings.units) is the durable one.
  }
}

export function usesImperial(locale: string = navigator.language): boolean {
  if (preference !== 'auto') return preference === 'imperial';
  return regionUsesImperial(locale);
}

/** What 'auto' resolves to on this device. */
export function regionUsesImperial(locale: string = navigator.language): boolean {
  try {
    const region = new Intl.Locale(locale).maximize().region;
    return region !== undefined && IMPERIAL_REGIONS.has(region);
  } catch {
    return /-US$/i.test(locale);
  }
}

/**
 * The phone-to-person distance the setup guide recommends. The metric range is
 * the product's (PRODUCT §3); the imperial one is it rounded to whole feet
 * (1.5–2.5 m ≈ 4.9–8.2 ft).
 */
export function cameraDistance(locale?: string): string {
  return usesImperial(locale) ? '5–8 ft' : '1.5–2.5 m';
}
